// src/components/FeedbackDialog.tsx
import * as React from "react";
import {LucideIcon} from "lucide-react";
import axiosInstance from "@/api/axios";
import { useTranslation } from "react-i18next";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";
import {SidebarMenuButton} from "@/components/ui/sidebar";
import {useState} from "react";

interface FeedbackDialogProps {
    icon: LucideIcon;
    title: string;
    onSubmitSuccess?: () => void;
}

export function FeedbackDialog({icon: Icon, title, onSubmitSuccess}: FeedbackDialogProps) {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        title: "",
        details: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [open, setOpen] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const {id, value} = e.target;
        setFormData(prev => ({...prev, [id]: value}));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Send feedback data using axiosInstance

            const csrfResponse = await axiosInstance.get("csrf/");
            const csrfToken = csrfResponse.data.csrfToken;

            const response = await axiosInstance.post("feature_request", formData, {
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                withCredentials: true,
            });

            console.log("Feature request posted successfully ", response);

            // Reset form and close dialog on success
            setFormData({title: "", details: ""});
            setOpen(false);

            // Call success callback if provided
            if (onSubmitSuccess) {
                onSubmitSuccess();
            }
        } catch (error) {
            console.error("Error submitting feedback:", error);
            // You could add additional error handling here, like displaying an error message
        } finally {
            setIsSubmitting(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <SidebarMenuButton>
                    <Icon/>
                    <span>{title}</span>
                </SidebarMenuButton>
            </DialogTrigger>
            <DialogContent
                className="w-[95vw] max-w-[425px] sm:max-w-[500px] md:max-w-[650px] lg:max-w-[750px] xl:max-w-[850px] 2xl:max-w-[950px] max-h-[90vh] overflow-y-auto">
                <DialogHeader className="space-y-3">
                    <DialogTitle className="text-xl font-semibold">{t('feedback.dialog.title')}</DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        {t('feedback.dialog.description')}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-6 py-6">
                        <div className="space-y-2">
                            <Label htmlFor="title" className="text-sm font-medium">
                                {t('feedback.form.title.label')}
                            </Label>
                            <Input
                                id="title"
                                placeholder={t('feedback.form.title.placeholder')}
                                className="w-full"
                                aria-describedby="title-description"
                                value={formData.title}
                                onChange={handleChange}
                                required
                            />
                            <p id="title-description" className="text-xs text-muted-foreground">
                                {t('feedback.form.title.description')}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="details" className="text-sm font-medium">
                                {t('feedback.form.details.label')}
                            </Label>
                            <Textarea
                                id="details"
                                placeholder={t('feedback.form.details.placeholder')}
                                className="w-full min-h-[120px] resize-y"
                                aria-describedby="details-description"
                                value={formData.details}
                                onChange={handleChange}
                                required
                            />
                            <p id="details-description" className="text-xs text-muted-foreground">
                                {t('feedback.form.details.description')}
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="gap-2 pt-4">
                        <Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
                            {isSubmitting ? t('feedback.form.submitting') : t('feedback.form.submit')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}